<template>
  <!-- Phase 4: cards + details/more + layered filter + bulk select. -->
  <div class="pml" :class="{ 'pml--selecting': selectionMode }" dir="rtl">
    <div class="pml__header">
      <div class="pml__bar">
        <div class="pml__bar-actions">
          <button
            v-if="paymentsList.type !== 'removedPayments'"
            type="button"
            class="pml__bar-btn pml__bar-btn--create"
            :aria-label="$t(paymentsList.type)"
            @click="goToNew"
          >
            <v-icon size="22" color="#5B7C99">mdi-plus</v-icon>
          </button>
          <button
            type="button"
            class="pml__bar-btn"
            :aria-label="$t('refresh')"
            @click="onRefreshTap"
          >
            <v-icon size="20" color="#37474F">mdi-refresh</v-icon>
          </button>
        </div>
        <h1 class="pml__title">
          {{ $t("list") }} {{ $t(paymentsList.type) }}
        </h1>
        <button
          type="button"
          class="pml__bar-btn pml__bar-btn--back"
          :aria-label="$t('back')"
          @click="goBack"
        >
          <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
        </button>
      </div>

      <div class="pml__search-row">
        <div class="pml__search">
          <v-icon size="20" color="#90A4AE" class="pml__search-icon">
            mdi-magnify
          </v-icon>
          <input
            v-model="paymentsList.search"
            type="search"
            class="pml__search-input"
            :placeholder="$t('search')"
            autocomplete="off"
          />
          <button
            v-if="paymentsList.search"
            type="button"
            class="pml__search-clear"
            :aria-label="$t('clear')"
            @click="paymentsList.search = ''"
          >
            <v-icon size="16" color="#90A4AE">mdi-close</v-icon>
          </button>
        </div>
        <button
          type="button"
          class="pml__filter-btn"
          :class="{ 'pml__filter-btn--active': hasAnyFilter }"
          :aria-label="$t('filter')"
          @click="onFilterTap"
        >
          <v-icon
            size="22"
            :color="hasAnyFilter ? '#1565C0' : '#546E7A'"
          >
            mdi-filter-outline
          </v-icon>
        </button>
        <button
          type="button"
          class="pml__filter-btn"
          :class="{ 'pml__filter-btn--active': selectionMode }"
          :aria-label="$t('select')"
          @click="toggleSelectionMode"
        >
          <v-icon size="22" :color="selectionMode ? '#1565C0' : '#546E7A'">
            mdi-checkbox-multiple-marked-outline
          </v-icon>
        </button>
      </div>

      <div v-if="selectionMode" class="pml__select-bar" dir="rtl">
        <button
          type="button"
          class="pml__select-bar-btn"
          @click="selectAllLoaded"
        >
          <v-icon size="17" color="#5B7C99">mdi-check-all</v-icon>
          {{ $t("selectAllItems") }}
        </button>
        <span class="pml__select-count">
          {{ selectedItems.length }} {{ $t("selectedCount") }}
        </span>
        <button
          type="button"
          class="pml__select-bar-btn"
          @click="clearSelection"
        >
          <v-icon size="17" color="#90A4AE">mdi-close</v-icon>
          {{ $t("deselectAll") }}
        </button>
      </div>
    </div>

    <div v-if="paymentsList.loading && !displayItems.length" class="pml__state">
      <v-progress-circular indeterminate color="#5B7C99" size="28" />
    </div>
    <div v-else-if="!displayItems.length" class="pml__state pml__state--empty">
      <img
        class="pml__empty-logo"
        src="@/assets/dashboard/no-data-cloud.png"
        alt=""
      />
      <p class="pml__empty-text">{{ $t("no-data") }}</p>
    </div>
    <div v-else class="pml__list">
      <article
        v-for="(item, index) in displayItems"
        :key="item.id || index"
        class="pml-card"
        :class="{
          'pml-card--selected': selectionMode && isSelected(item),
        }"
      >
        <div
          v-if="selectionMode"
          class="pml-card__select-overlay"
          :class="{ 'pml-card__select-overlay--on': isSelected(item) }"
          @click="toggleSelect(item)"
        >
          <span v-if="isSelected(item)" class="pml-card__check">
            <v-icon size="17" color="#FFFFFF">mdi-check</v-icon>
          </span>
        </div>
        <div class="pml-card__head">
          <div class="pml-card__title-block">
            <button
              type="button"
              class="pml-card__name-link"
              :disabled="!!item.lock"
              @click="paymentsList.edit(item)"
            >
              <span class="pml-card__name">
                {{ item.account && item.account.name ? item.account.name : "—" }}
              </span>
              <v-icon
                v-if="item.lock"
                size="14"
                color="#E53935"
                class="ms-1"
              >
                mdi-lock-outline
              </v-icon>
              <v-icon v-else size="12" color="#879EB6">mdi-pencil-outline</v-icon>
            </button>
            <div class="pml-card__sub">
              {{ $t("date") }}: {{ item.date || "—" }}
            </div>
          </div>
          <span class="pml-card__number">#{{ item.id }}</span>
        </div>

        <div class="pml-card__amounts">
          <div class="pml-card__amount-row">
            <span class="pml-card__amount-label">{{ $t("amount") }}</span>
            <span
              class="pml-card__amount-value"
              :class="{
                'pml-card__amount-value--pay': item.type === 'pay',
                'pml-card__amount-value--receive': item.type === 'recieve',
              }"
            >
              {{ amountText(item) }}
            </span>
          </div>
          <div class="pml-card__amount-row">
            <span class="pml-card__amount-label">
              {{ $t("bank") }}/{{ $t("cash") }}
            </span>
            <span class="pml-card__amount-value">
              {{ item.bank && item.bank.name ? item.bank.name : "—" }}
            </span>
          </div>
          <div class="pml-card__amount-row">
            <span class="pml-card__amount-label">{{ $t("document-number") }}</span>
            <span class="pml-card__amount-value">
              {{
                item.doc_header && item.doc_header.number
                  ? item.doc_header.number
                  : "—"
              }}
            </span>
          </div>
        </div>

        <div v-if="!selectionMode" class="pml-card__footer">
          <div class="pml-card__actions">
            <button
              v-show="
                paymentsList.type !== 'removedPayments' &&
                removePermission(paymentsList.type)
              "
              type="button"
              class="pml-btn pml-btn--danger"
              :disabled="!!item.lock"
              @click="askDelete(item)"
            >
              <v-icon size="14" color="#E53935">mdi-trash-can-outline</v-icon>
              {{ $t("remove") }}
            </button>
            <button
              v-if="paymentsList.type !== 'removedPayments'"
              type="button"
              class="pml-btn pml-btn--primary"
              :disabled="!!item.lock"
              @click="paymentsList.edit(item)"
            >
              <v-icon size="14" color="#FFFFFF">mdi-pencil-outline</v-icon>
              {{ $t("edit") }}
            </button>
            <button
              type="button"
              class="pml-btn pml-btn--outline"
              @click="openDetails(item)"
            >
              <v-icon size="14" color="#5B7C99">mdi-eye-outline</v-icon>
              {{ $t("details") }}
            </button>
          </div>
          <button
            type="button"
            class="pml-rel-btn"
            :disabled="!hasRelations(item)"
            @click="openRelationsSheet(item)"
          >
            <v-icon
              size="14"
              :color="hasRelations(item) ? '#5B7C99' : '#90A4AE'"
            >
              mdi-link-variant
            </v-icon>
            {{ $t("relations") }}
          </button>
        </div>
      </article>
      <div v-if="appending" class="pml__state pml__state--more">
        <v-progress-circular indeterminate color="#5B7C99" size="22" />
      </div>
    </div>

    <div ref="sentinel" class="pml__sentinel" />

    <div v-if="selectionMode" class="pml-bulkbar" dir="rtl">
      <button
        v-if="paymentsList.receiptKind"
        type="button"
        class="pml-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="paymentsList.receiptPrint(null, selectedItems)"
      >
        <v-icon size="19" color="#5B7C99">mdi-printer-outline</v-icon>
        {{ $t("receiptGroupPrint") }}
      </button>
      <button
        v-if="
          lockPermission && paymentsList.type !== 'removedPayments'
        "
        type="button"
        class="pml-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkLock('lock')"
      >
        <v-icon size="19" color="#5B7C99">mdi-lock-outline</v-icon>
        {{ $t("lock") }}
      </button>
      <button
        v-if="
          lockPermission && paymentsList.type !== 'removedPayments'
        "
        type="button"
        class="pml-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkLock('unlock')"
      >
        <v-icon size="19" color="#5B7C99">mdi-lock-open-outline</v-icon>
        {{ $t("unlock") }}
      </button>
      <button
        v-if="paymentsList.type === 'removedPayments'"
        type="button"
        class="pml-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkRefreshRemoved"
      >
        <v-icon size="19" color="#5B7C99">mdi-delete-restore</v-icon>
        {{ $t("refreshRemovedItem") }}
      </button>
      <button
        v-if="
          paymentsList.type !== 'removedPayments' &&
          removePermission(paymentsList.type)
        "
        type="button"
        class="pml-bulkbar__btn pml-bulkbar__btn--danger"
        :disabled="!selectedItems.length"
        @click="askGroupDelete"
      >
        <v-icon size="19" color="#E53935">mdi-trash-can-outline</v-icon>
        {{ $t("groupDelete") }}
      </button>
    </div>

    <v-bottom-sheet v-model="detailSheet" scrollable>
      <v-card class="pml-sheet">
        <div class="pml-sheet__handle" />
        <div class="pml-sheet__header" dir="rtl">
          <div class="pml-sheet__header-side">
            <span v-if="detailItem" class="pml-sheet__num-chip">
              #{{ detailItem.id }}
            </span>
          </div>
          <div class="pml-sheet__title">{{ $t("details") }}</div>
          <div class="pml-sheet__header-side pml-sheet__header-side--end">
            <button
              type="button"
              class="pml-sheet__back"
              :aria-label="$t('back')"
              @click="detailSheet = false"
            >
              <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
            </button>
          </div>
        </div>

        <div
          v-if="detailItem && paymentsList.type !== 'removedPayments'"
          class="pml-sheet__quickbar"
          dir="rtl"
        >
          <button
            type="button"
            class="pml-sheet__quick"
            :disabled="!!detailItem.lock"
            @click="runNamedAction('edit')"
          >
            <v-icon size="18" color="#5B7C99">mdi-pencil-outline</v-icon>
            {{ $t("edit") }}
          </button>
          <button
            v-if="detailItem.doc_head_id"
            type="button"
            class="pml-sheet__quick"
            @click="runNamedAction('viewDoc')"
          >
            <v-icon size="18" color="#5B7C99">mdi-file-eye-outline</v-icon>
            {{ $t("viewAccountingDoc") }}
          </button>
          <button
            v-if="paymentsList.receiptKind"
            type="button"
            class="pml-sheet__quick"
            @click="runNamedAction('receipt')"
          >
            <v-icon size="18" color="#5B7C99">mdi-receipt-text-outline</v-icon>
            {{ $t("treasuryReceiptPrint") }}
          </button>
          <button
            v-if="lockPermission"
            type="button"
            class="pml-sheet__quick"
            @click="runNamedAction('lock')"
          >
            <v-icon
              size="18"
              :color="detailItem.lock ? 'green' : 'pink'"
            >
              {{
                detailItem.lock
                  ? "mdi-lock-open-outline"
                  : "mdi-lock-outline"
              }}
            </v-icon>
            {{ detailItem.lock ? $t("unlock") : $t("lock") }}
          </button>
          <button
            type="button"
            class="pml-sheet__quick"
            v-clipboard:copy="editLink(detailItem)"
            @click="onCopyLinkQuick"
          >
            <v-icon size="18" color="#5B7C99">mdi-link</v-icon>
            {{ $t("copy-link") }}
          </button>
          <button
            type="button"
            class="pml-sheet__quick"
            @click="moreSheet = true"
          >
            <v-icon size="18" color="#5B7C99">mdi-dots-horizontal</v-icon>
            {{ $t("more") }}
          </button>
        </div>

        <div v-if="detailItem" class="pml-sheet__name-block" dir="rtl">
          <div class="pml-sheet__name-row">
            <span class="pml-sheet__row-label">{{ $t("account") }}</span>
            <span class="pml-sheet__name-value">
              {{
                detailItem.account && detailItem.account.name
                  ? detailItem.account.name
                  : "—"
              }}
            </span>
          </div>
          <button
            v-if="detailItem.account && detailItem.account.fin_id"
            type="button"
            class="pml-sheet__linkrow"
            @click="runNamedAction('accountTurnover')"
          >
            <v-icon size="18" color="#00796B">mdi-playlist-check</v-icon>
            <span class="pml-sheet__linkrow-text">{{ $t("turnover") }}</span>
            <v-icon size="18" color="#80CBC4">mdi-chevron-left</v-icon>
          </button>
        </div>

        <div v-if="detailItem" class="pml-sheet__body" dir="rtl">
          <div class="pml-sheet__card">
            <div
              v-for="(row, i) in detailRows(detailItem)"
              :key="'detail-row-' + i"
              class="pml-sheet__row"
              :class="{
                'pml-sheet__row--band': row.kind === 'band' || row.kind === 'amount',
                'pml-sheet__row--pay': row.kind === 'amount' && row.tone === 'pay',
                'pml-sheet__row--receive':
                  row.kind === 'amount' && row.tone === 'receive',
              }"
            >
              <span class="pml-sheet__row-label">{{ row.label }}</span>
              <span class="pml-sheet__row-value">{{ row.value }}</span>
            </div>
            <div class="pml-sheet__row">
              <span class="pml-sheet__row-label">{{ $t("relation-whit") }}</span>
              <span
                v-if="detailItem.relations && detailItem.relations.length"
                class="pml-sheet__tags"
              >
                <v-chip
                  v-for="(relation, ri) in detailItem.relations"
                  :key="'detail-rel-' + ri"
                  :color="relation.color || '#607D8B'"
                  label
                  outlined
                  small
                  class="px-1"
                >
                  {{ relation.name }}
                </v-chip>
              </span>
              <span v-else class="pml-sheet__row-value">—</span>
            </div>
            <div class="pml-sheet__row">
              <span class="pml-sheet__row-label">{{ $t("tag") }}</span>
              <span
                v-if="detailItem.tags && detailItem.tags.length"
                class="pml-sheet__tags"
              >
                <v-chip
                  v-for="(tag, ti) in detailItem.tags"
                  :key="'detail-tag-' + ti"
                  :color="tag.color || '#607D8B'"
                  label
                  small
                  class="px-1"
                >
                  <span class="px-1 white--text">{{ tag.text }}</span>
                </v-chip>
              </span>
              <span v-else class="pml-sheet__row-value">—</span>
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="moreSheet">
      <v-card class="pml-sheet">
        <div class="pml-sheet__handle" />
        <div class="pml-sheet__header" dir="rtl">
          <span class="pml-sheet__header-spacer" />
          <div class="pml-sheet__title">{{ $t("more") }}</div>
          <button
            type="button"
            class="pml-sheet__back"
            :aria-label="$t('back')"
            @click="moreSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div v-if="detailItem" class="pml-sheet__body" dir="rtl">
          <div class="pml-sheet__actions pml-sheet__actions--sheet">
            <div class="pml-sheet__action pml-sheet__action--log">
              <log
                :title-prop="$t('history')"
                icon-prop="mdi-history"
                :listProp="true"
                :type-prop="logTypeProp"
                :refIdProp="detailItem.id"
                mode-prop="button"
              />
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Relations layer: same number/amount rows as desktop chips -->
    <v-bottom-sheet v-model="relationsSheet" scrollable>
      <v-card class="pml-sheet">
        <div class="pml-sheet__handle" />
        <div class="pml-sheet__header" dir="rtl">
          <div class="pml-sheet__header-side">
            <span v-if="relationsItem" class="pml-sheet__num-chip">
              #{{ relationsItem.id }}
            </span>
          </div>
          <div class="pml-sheet__title">{{ $t("relations") }}</div>
          <div class="pml-sheet__header-side pml-sheet__header-side--end">
            <button
              type="button"
              class="pml-sheet__back"
              :aria-label="$t('back')"
              @click="relationsSheet = false"
            >
              <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
            </button>
          </div>
        </div>

        <div v-if="relationsItem" class="pml-sheet__body" dir="rtl">
          <div
            v-if="!(relationsItem.relations && relationsItem.relations.length)"
            class="pml-rel-empty"
          >
            —
          </div>
          <div
            v-for="(row, i) in relationRows(relationsItem)"
            :key="'rel-' + i"
            class="pml-rel-item"
          >
            <div class="pml-rel-item__title">{{ row.typeLabel }}</div>
            <div class="pml-rel-item__line">
              <span class="pml-rel-item__key">{{ $t("number") }}</span>
              <span class="pml-rel-item__val">{{ row.number }}</span>
            </div>
            <div class="pml-rel-item__line">
              <span class="pml-rel-item__key">{{ $t("amount") }}</span>
              <span class="pml-rel-item__val">{{ row.amountText }}</span>
            </div>
          </div>
        </div>

        <div class="pml-sheet__footer" dir="rtl">
          <button
            type="button"
            class="pml-sheet__btn pml-sheet__btn--primary"
            @click="relationsSheet = false"
          >
            {{ $t("back") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Layered filter: same shell fields as desktop filter dialog -->
    <v-bottom-sheet v-model="filterSheet" scrollable>
      <v-card class="pml-sheet pml-sheet--filter">
        <div class="pml-sheet__handle" />
        <div class="pml-sheet__header" dir="rtl">
          <span class="pml-sheet__header-spacer" />
          <div class="pml-sheet__title">{{ $t("filter") }}</div>
          <button
            type="button"
            class="pml-sheet__back"
            :aria-label="$t('back')"
            @click="filterSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="pml-filter-nav" dir="rtl">
          <button
            v-for="group in filterGroups"
            :key="group.key"
            type="button"
            class="pml-filter-nav__item"
            @click="openFilterGroup(group.key)"
          >
            <v-icon class="pml-filter-nav__icon" size="22" color="#5B7C99">
              {{ group.icon }}
            </v-icon>
            <span class="pml-filter-nav__texts">
              <span class="pml-filter-nav__item-title">{{ group.title }}</span>
              <span class="pml-filter-nav__item-sub">
                {{ groupSubtitle(group.key) }}
              </span>
            </span>
            <span v-if="groupHasValue(group.key)" class="pml-filter-nav__dot" />
            <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>
        </div>

        <div class="pml-sheet__footer" dir="rtl">
          <button
            type="button"
            class="pml-sheet__btn pml-sheet__btn--primary"
            @click="applyFilter"
          >
            <v-icon size="16" color="#FFFFFF">mdi-filter-outline</v-icon>
            {{ $t("filter") }}
          </button>
          <button
            type="button"
            class="pml-sheet__btn pml-sheet__btn--ghost"
            @click="clearFilter"
          >
            <v-icon size="16" color="#5B7C99">mdi-filter-remove-outline</v-icon>
            {{ $t("clear") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="filterGroupSheet" scrollable>
      <v-card class="pml-sheet pml-sheet--filter">
        <div class="pml-sheet__handle" />
        <div class="pml-sheet__header" dir="rtl">
          <span class="pml-sheet__header-spacer" />
          <div class="pml-sheet__title">{{ activeFilterGroupTitle }}</div>
          <button
            type="button"
            class="pml-sheet__back"
            :aria-label="$t('back')"
            @click="backToFilterMain"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="pml-filter-fields" dir="rtl">
          <template v-if="filterGroup === 'parties'">
            <div class="pml-f-field-wrap">
              <div class="pml-f-field-wrap__label">{{ $t("account") }}</div>
              <button
                type="button"
                class="pml-f-picker-field"
                @click="openPicker('pmAccount')"
              >
                <span class="pml-f-picker-field__text">
                  {{ pickerSummary("pmAccount", paymentsList.account) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="pml-picker-host" aria-hidden="true">
                <autocomplete
                  ref="pmAccount"
                  v-model="paymentsList.account"
                  :title="$t('account')"
                  type="person"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
            <div class="pml-f-field-wrap">
              <div class="pml-f-field-wrap__label">
                {{ $t("bank") }}/{{ $t("cash") }}
              </div>
              <button
                type="button"
                class="pml-f-picker-field"
                @click="openPicker('pmBank')"
              >
                <span class="pml-f-picker-field__text">
                  {{ pickerSummary("pmBank", paymentsList.bank) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="pml-picker-host" aria-hidden="true">
                <autocomplete
                  ref="pmBank"
                  v-model="paymentsList.bank"
                  :title="$t('bank') + '/' + $t('cash')"
                  type="allBankCash"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
            <div class="pml-f-field-wrap">
              <div class="pml-f-field-wrap__label">{{ $t("currency") }}</div>
              <button
                type="button"
                class="pml-f-picker-field"
                @click="openPicker('pmCurrency')"
              >
                <span class="pml-f-picker-field__text">
                  {{ pickerSummary("pmCurrency", paymentsList.currencyFilter) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="pml-picker-host" aria-hidden="true">
                <autocomplete
                  ref="pmCurrency"
                  v-model="paymentsList.currencyFilter"
                  :title="$t('currency')"
                  type="currency"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
          </template>

          <template v-else-if="filterGroup === 'amount'">
            <div
              v-for="field in numpadFields"
              :key="field.key"
              class="pml-f-field-wrap"
            >
              <div class="pml-f-field-wrap__label">{{ field.label }}</div>
              <button
                type="button"
                class="pml-f-picker-field"
                @click="openNumpad(field)"
              >
                <span class="pml-f-picker-field__text">
                  {{ numpadFieldText(field.key) }}
                </span>
                <span
                  v-if="hasValue(paymentsList[field.key])"
                  class="pml-picker-cover__clear"
                  @click.stop="paymentsList[field.key] = null"
                >
                  <v-icon size="18" color="#90A4AE">mdi-close</v-icon>
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
          </template>

          <template v-else-if="filterGroup === 'date'">
            <div
              v-for="field in dateFields"
              :key="field.key"
              class="pml-f-field-wrap"
            >
              <div class="pml-f-field-wrap__label">{{ field.label }}</div>
              <button
                type="button"
                class="pml-f-picker-field"
                @click="openDateLayer(field)"
              >
                <span class="pml-f-picker-field__text">
                  {{
                    hasValue(paymentsList[field.key])
                      ? paymentsList[field.key]
                      : "—"
                  }}
                </span>
                <span
                  v-if="hasValue(paymentsList[field.key])"
                  class="pml-picker-cover__clear"
                  @click.stop="paymentsList[field.key] = null"
                >
                  <v-icon size="18" color="#90A4AE">mdi-close</v-icon>
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
          </template>

          <template v-else-if="filterGroup === 'category'">
            <div class="pml-f-field-wrap">
              <div class="pml-f-field-wrap__label">{{ $t("tag") }}</div>
              <button
                type="button"
                class="pml-f-picker-field"
                @click="openPicker('pmTag')"
              >
                <span class="pml-f-picker-field__text">
                  {{ pickerSummary("pmTag", paymentsList.tag) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="pml-picker-host" aria-hidden="true">
                <tag ref="pmTag" v-model="paymentsList.tag" />
              </div>
            </div>
            <div class="pml-f-field-wrap">
              <div class="pml-f-field-wrap__label">{{ $t("branch") }}</div>
              <button
                type="button"
                class="pml-f-picker-field"
                @click="openPicker('pmBranch')"
              >
                <span class="pml-f-picker-field__text">
                  {{ pickerSummary("pmBranch", paymentsList.branch) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="pml-picker-host" aria-hidden="true">
                <autocomplete
                  ref="pmBranch"
                  v-model="paymentsList.branch"
                  title="branch"
                  type="branch"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
            <div class="pml-f-field-wrap">
              <div class="pml-f-field-wrap__label">
                {{ $t("accountCategory") }}
              </div>
              <button
                type="button"
                class="pml-f-picker-field"
                @click="openPicker('pmAccountCategory')"
              >
                <span class="pml-f-picker-field__text">
                  {{
                    pickerSummary(
                      "pmAccountCategory",
                      paymentsList.accountCategory
                    )
                  }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="pml-picker-host" aria-hidden="true">
                <autocomplete
                  ref="pmAccountCategory"
                  v-model="paymentsList.accountCategory"
                  :title="$t('accountCategory')"
                  type="accountCategory"
                  :multiple="true"
                  :filter="true"
                />
              </div>
            </div>
            <div class="pml-f-field-wrap">
              <div class="pml-f-field-wrap__label">{{ $t("registerar") }}</div>
              <button
                type="button"
                class="pml-f-picker-field"
                @click="openPicker('pmRegisterar')"
              >
                <span class="pml-f-picker-field__text">
                  {{ pickerSummary("pmRegisterar", paymentsList.registerar) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="pml-picker-host" aria-hidden="true">
                <autocomplete
                  ref="pmRegisterar"
                  v-model="paymentsList.registerar"
                  :title="$t('registerar')"
                  type="partners"
                  :multiple="true"
                  :filter="true"
                />
              </div>
            </div>
          </template>

          <template v-else-if="filterGroup === 'status'">
            <div class="pml-f-field-wrap">
              <div class="pml-f-field-wrap__label">
                {{ $t("lockStatusDocuments") }}
              </div>
              <button
                type="button"
                class="pml-f-picker-field"
                @click="
                  openSelectLayer(
                    'lockStatus',
                    $t('lockStatusDocuments'),
                    paymentsList.lockStatusItems || [],
                    'text'
                  )
                "
              >
                <span class="pml-f-picker-field__text">
                  {{
                    selectSummary(
                      "lockStatus",
                      paymentsList.lockStatusItems || [],
                      "text"
                    )
                  }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
          </template>
        </div>

        <div class="pml-sheet__footer" dir="rtl">
          <button
            type="button"
            class="pml-sheet__btn pml-sheet__btn--primary"
            @click="applyFilter"
          >
            <v-icon size="16" color="#FFFFFF">mdi-filter-outline</v-icon>
            {{ $t("filter") }}
          </button>
          <button
            type="button"
            class="pml-sheet__btn pml-sheet__btn--ghost"
            @click="backToFilterMain"
          >
            {{ $t("back") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="dateLayerOpen">
      <v-card class="pml-sheet pml-sheet--filter">
        <div class="pml-sheet__handle" />
        <div class="pml-sheet__header" dir="rtl">
          <span class="pml-sheet__header-spacer" />
          <div class="pml-sheet__title">{{ dateLayerLabel }}</div>
          <button
            type="button"
            class="pml-sheet__back"
            :aria-label="$t('back')"
            @click="dateLayerOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="pml-mobile-date" dir="rtl">
          <div class="pml-mobile-date__selected">
            <span class="pml-mobile-date__label">{{ dateLayerLabel }}</span>
            <strong class="pml-mobile-date__value">
              {{
                hasValue(paymentsList[dateLayerField])
                  ? paymentsList[dateLayerField]
                  : "—"
              }}
            </strong>
            <button
              v-if="hasValue(paymentsList[dateLayerField])"
              type="button"
              class="pml-mobile-date__clear"
              @click="paymentsList[dateLayerField] = null"
            >
              {{ $t("clear") }}
            </button>
          </div>
          <date-picker
            v-if="dateLayerField"
            :key="dateLayerField"
            v-model="paymentsList[dateLayerField]"
            inline
            simple
            auto-submit
            :type="dateLayerIsTime ? 'time' : 'date'"
            :locale="$store.state.date_locale"
            :format="dateLayerIsTime ? 'HH:mm' : $store.state.date_format"
            :display-format="dateLayerIsTime ? 'HH' : undefined"
            :min="dateLayerMin"
            class="pml-mobile-date__picker"
            color="#7B8BA3"
          />
        </div>

        <div class="pml-sheet__footer" dir="rtl">
          <button
            type="button"
            class="pml-sheet__btn pml-sheet__btn--primary"
            @click="dateLayerOpen = false"
          >
            {{ $t("Save") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="selectLayerOpen">
      <v-card class="pml-sheet pml-sheet--filter">
        <div class="pml-sheet__handle" />
        <div class="pml-sheet__header" dir="rtl">
          <span class="pml-sheet__header-spacer" />
          <div class="pml-sheet__title">{{ selectLayerTitle }}</div>
          <button
            type="button"
            class="pml-sheet__back"
            :aria-label="$t('back')"
            @click="selectLayerOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="pml-select-layer" dir="rtl">
          <button
            v-for="item in selectLayerItems"
            :key="item.value"
            type="button"
            class="pml-select-layer__item"
            @click="selectLayerPick(item.value)"
          >
            <span class="pml-select-layer__text">
              {{ item[selectLayerTextKey] }}
            </span>
            <v-icon
              v-if="
                selectLayerField &&
                paymentsList[selectLayerField] === item.value
              "
              size="20"
              color="#5B7C99"
            >
              mdi-check
            </v-icon>
          </button>
          <button
            v-if="selectLayerField && hasValue(paymentsList[selectLayerField])"
            type="button"
            class="pml-select-layer__clear"
            @click="selectLayerPick(null)"
          >
            {{ $t("clear") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="deleteSheet">
      <v-card class="pml-confirm-sheet" dir="rtl">
        <div class="pml-confirm-sheet__handle" />
        <div class="pml-confirm-sheet__icon">
          <v-icon size="26" color="#E53935">mdi-trash-can-outline</v-icon>
        </div>
        <div class="pml-confirm-sheet__title">
          {{ deleteGroupMode ? $t("groupDelete") : $t("delete") }}
        </div>
        <p class="pml-confirm-sheet__subtitle">
          {{ $t("are-you-sure-delete-this-case") }}
        </p>
        <div v-if="deleteGroupMode" class="pml-confirm-sheet__rows">
          <div class="pml-confirm-sheet__row">
            <span>{{ $t("totalCount") }}</span>
            <strong>{{ selectedItems.length }}</strong>
          </div>
          <div class="pml-confirm-sheet__items">
            <div
              v-for="(sel, si) in selectedItems"
              :key="'del-item-' + si"
              class="pml-confirm-sheet__item"
            >
              <span class="pml-confirm-sheet__item-main">
                <span class="pml-confirm-sheet__item-index">
                  {{ selectedRowNumber(sel) }}
                </span>
                <span class="pml-confirm-sheet__item-name">
                  {{
                    sel.account && sel.account.name ? sel.account.name : "—"
                  }}
                </span>
              </span>
              <span class="pml-confirm-sheet__item-number">#{{ sel.id }}</span>
            </div>
          </div>
        </div>
        <div
          v-else-if="paymentsList.deleteItem"
          class="pml-confirm-sheet__rows"
        >
          <div class="pml-confirm-sheet__row">
            <span>{{ $t("account") }}</span>
            <strong>
              {{
                paymentsList.deleteItem.account &&
                paymentsList.deleteItem.account.name
                  ? paymentsList.deleteItem.account.name
                  : "—"
              }}
            </strong>
          </div>
          <div class="pml-confirm-sheet__row">
            <span>
              {{ $t("number") }} {{ $t(paymentsList.type) }}
            </span>
            <strong>{{ paymentsList.deleteItem.id }}</strong>
          </div>
          <div
            v-if="paymentsList.deleteItem.date"
            class="pml-confirm-sheet__row"
          >
            <span>{{ $t("date") }}</span>
            <strong>{{ paymentsList.deleteItem.date }}</strong>
          </div>
          <div class="pml-confirm-sheet__row">
            <span>{{ $t("amount") }}</span>
            <strong>{{ amountText(paymentsList.deleteItem) }}</strong>
          </div>
          <div
            v-if="paymentsList.deleteItem.bank && paymentsList.deleteItem.bank.name"
            class="pml-confirm-sheet__row"
          >
            <span>{{ $t("bank") }} / {{ $t("cash") }}</span>
            <strong>{{ paymentsList.deleteItem.bank.name }}</strong>
          </div>
        </div>
        <div class="pml-confirm-sheet__actions">
          <v-btn
            color="error"
            block
            depressed
            height="48"
            class="pml-confirm-sheet__yes"
            @click="confirmDelete"
          >
            {{ $t("yes") }}
          </v-btn>
          <v-btn
            outlined
            block
            height="48"
            color="secondary"
            class="pml-confirm-sheet__no"
            @click="cancelDelete"
          >
            {{ $t("no") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="numpadOpen" @click:outside="numpadOpen = false">
      <v-card class="pml-numpad" dir="rtl">
        <div class="pml-sheet__handle" />
        <div class="pml-numpad__header">
          <span class="pml-numpad__header-spacer" />
          <div class="pml-numpad__title">{{ numpadLabel }}</div>
          <button
            type="button"
            class="pml-sheet__back pml-numpad__back"
            :aria-label="$t('back')"
            @click="numpadOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="pml-numpad__display">
          <div class="pml-numpad__display-label">{{ numpadLabel }}</div>
          <div class="pml-numpad__display-value">{{ numpadDisplay }}</div>
        </div>

        <div class="pml-numpad__pad" dir="ltr">
          <div class="pml-numpad__digits">
            <button
              v-for="n in [7, 8, 9, 4, 5, 6, 1, 2, 3]"
              :key="'d-' + n"
              type="button"
              class="pml-numpad__key"
              @click="numpadDigit(n)"
            >
              {{ n }}
            </button>
            <button
              type="button"
              class="pml-numpad__key pml-numpad__key--multi"
              @click="numpadAppend('00')"
            >
              00
            </button>
            <button
              type="button"
              class="pml-numpad__key"
              @click="numpadDigit(0)"
            >
              0
            </button>
            <button
              type="button"
              class="pml-numpad__key pml-numpad__key--multi"
              @click="numpadAppend('000')"
            >
              000
            </button>
          </div>
          <div class="pml-numpad__actions">
            <button
              type="button"
              class="pml-numpad__key pml-numpad__key--action"
              @click="numpadStep(1)"
            >
              +
            </button>
            <button
              type="button"
              class="pml-numpad__key pml-numpad__key--action"
              @click="numpadStep(-1)"
            >
              -
            </button>
            <button
              type="button"
              class="pml-numpad__key pml-numpad__key--action"
              @click="numpadClear"
            >
              C
            </button>
            <button
              type="button"
              class="pml-numpad__key pml-numpad__key--action"
              @click="numpadDot"
            >
              .
            </button>
            <button
              type="button"
              class="pml-numpad__key pml-numpad__key--action"
              @click="numpadBackspace"
            >
              <v-icon size="20" color="#5B7C99">mdi-backspace-outline</v-icon>
            </button>
          </div>
        </div>

        <div class="pml-numpad__footer">
          <button
            type="button"
            class="pml-numpad__confirm"
            @click="confirmNumpad"
          >
            {{ $t("confirm") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
export default {
  name: "PaymentsListMobile",
  inject: ["paymentsList"],
  components: {
    autocomplete: () =>
      import("../../../../components/general/patial/autocomplete"),
    Tag: () => import("../../../../components/general/Tag/tag"),
  },
  data() {
    return {
      displayItems: [],
      appending: false,
      observer: null,
      detailSheet: false,
      moreSheet: false,
      detailItem: null,
      relationsSheet: false,
      relationsItem: null,
      filterSheet: false,
      filterGroupSheet: false,
      filterGroup: null,
      numpadOpen: false,
      numpadField: null,
      numpadLabel: "",
      numpadDraft: "",
      numpadIsAmount: false,
      dateLayerOpen: false,
      dateLayerField: null,
      dateLayerLabel: "",
      dateLayerIsTime: false,
      selectLayerOpen: false,
      selectLayerField: null,
      selectLayerTitle: "",
      selectLayerItems: [],
      selectLayerTextKey: "text",
      selectionMode: false,
      selectedItems: [],
      _selectionSynced: false,
      deleteSheet: false,
      deleteGroupMode: false,
    };
  },
  computed: {
    currentPage() {
      const opts = this.paymentsList.tableOptions || {};
      return opts.page || 1;
    },
    logTypeProp() {
      return this.paymentsList.typeForm || this.paymentsList.type || "";
    },
    hasAnyFilter() {
      return (
        this.groupHasValue("parties") ||
        this.groupHasValue("amount") ||
        this.groupHasValue("date") ||
        this.groupHasValue("category") ||
        this.groupHasValue("status")
      );
    },
    filterGroups() {
      return [
        {
          key: "parties",
          icon: "mdi-account-outline",
          title: this.$t("account") + " / " + this.$t("bank"),
        },
        {
          key: "amount",
          icon: "mdi-cash",
          title: this.$t("amount"),
        },
        {
          key: "date",
          icon: "mdi-calendar",
          title: this.$t("date"),
        },
        {
          key: "category",
          icon: "mdi-shape-outline",
          title: this.$t("tag") + " / " + this.$t("branch"),
        },
        {
          key: "status",
          icon: "mdi-lock-outline",
          title: this.$t("status"),
        },
      ];
    },
    activeFilterGroupTitle() {
      const group = this.filterGroups.find((g) => g.key === this.filterGroup);
      return group ? group.title : this.$t("filter");
    },
    numpadFields() {
      return [
        {
          key: "fromAmount",
          label: this.$t("from") + " " + this.$t("amount"),
          isAmount: true,
        },
        {
          key: "toAmount",
          label: this.$t("until") + " " + this.$t("amount"),
          isAmount: true,
        },
      ];
    },
    dateFields() {
      return [
        {
          key: "fromDate",
          label: this.$t("from") + " " + this.$t("date"),
          isTime: false,
        },
        {
          key: "toDate",
          label: this.$t("until") + " " + this.$t("date"),
          isTime: false,
        },
        { key: "fromTime", label: this.$t("from-hour"), isTime: true },
        { key: "toTime", label: this.$t("to-hour"), isTime: true },
      ];
    },
    dateLayerMin() {
      if (this.dateLayerIsTime || !this.dateLayerField) return undefined;
      if (this.dateLayerField === "toDate") {
        return this.paymentsList.fromDate || undefined;
      }
      return undefined;
    },
    numpadDisplay() {
      const raw = String(this.numpadDraft == null ? "" : this.numpadDraft);
      if (!raw) return "0";
      if (!this.numpadIsAmount) return raw;
      if (raw.endsWith(".")) {
        const base = raw.slice(0, -1) || "0";
        const formatted = this.$numberFormat
          ? this.$numberFormat(base, "plain", 0)
          : base;
        return formatted + ".";
      }
      return this.$numberFormat ? this.$numberFormat(raw, "plain", 0) : raw;
    },
  },
  watch: {
    "paymentsList.items"(val) {
      const items = val || [];
      if (this.appending) {
        const seen = {};
        this.displayItems.forEach((i) => {
          seen[i.id] = true;
        });
        this.displayItems = this.displayItems.concat(
          items.filter((i) => !seen[i.id])
        );
        this.appending = false;
      } else {
        this.displayItems = items.slice();
      }
    },
    "paymentsList.search"() {
      this.appending = false;
      if (this.currentPage !== 1) {
        this.paymentsList.tableOptions = Object.assign(
          {},
          this.paymentsList.tableOptions,
          { page: 1 }
        );
      }
    },
    "paymentsList.selected"(val) {
      if (!this.selectionMode || !this._selectionSynced) return;
      if (!val || !val.length) {
        this._selectionSynced = false;
        this.exitSelectionMode();
      }
    },
    deleteSheet(val) {
      if (!val) this.deleteGroupMode = false;
    },
  },
  mounted() {
    this.displayItems = (this.paymentsList.items || []).slice();
    this.paymentsList.tableOptions = Object.assign(
      {},
      this.paymentsList.tableOptions,
      { page: 1, itemsPerPage: 10 }
    );
    this.$nextTick(() => {
      if (!("IntersectionObserver" in window) || !this.$refs.sentinel) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0] && entries[0].isIntersecting) this.loadMore();
        },
        { rootMargin: "200px" }
      );
      this.observer.observe(this.$refs.sentinel);
    });
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
  methods: {
    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.push({ name: "Home" }).catch(() => {});
    },
    goToNew() {
      const type = this.paymentsList.type;
      if (!type || type === "removedPayments") return;
      if (typeof this.paymentsList.goTo === "function") {
        this.paymentsList.goTo(type);
      }
    },
    onRefreshTap() {
      this.appending = false;
      this.paymentsList.tableOptions = Object.assign(
        {},
        this.paymentsList.tableOptions,
        { page: 1 }
      );
      this.paymentsList.getItems();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onFilterTap() {
      this.filterSheet = true;
    },
    openPicker(refName) {
      const comp = this.$refs[refName];
      if (comp && typeof comp.openMobileDialog === "function") {
        comp.openMobileDialog();
      }
    },
    openSelectLayer(field, title, items, textKey) {
      this.selectLayerField = field;
      this.selectLayerTitle = title;
      this.selectLayerItems = items || [];
      this.selectLayerTextKey = textKey || "text";
      this.selectLayerOpen = true;
    },
    selectLayerPick(value) {
      if (this.selectLayerField) {
        this.paymentsList[this.selectLayerField] = value;
      }
      this.selectLayerOpen = false;
    },
    selectSummary(field, items, textKey) {
      const v = this.paymentsList[field];
      if (!this.hasValue(v)) return "—";
      const found = (items || []).find((i) => i && i.value === v);
      return found ? found[textKey] : "—";
    },
    pickerSummary(refName, value) {
      if (!this.hasValue(value)) return "—";
      const comp = this.$refs[refName];
      const items = (comp && comp.items) || [];
      const arr = Array.isArray(value) ? value : [value];
      const names = [];
      arr.forEach((v) => {
        if (v && typeof v === "object") {
          if (v.name || v.text) names.push(v.name || v.text);
        } else {
          const found = items.find((i) => i && i.id === v);
          if (found && found.name) names.push(found.name);
        }
      });
      if (names.length) return names.join(" · ");
      return String(arr.length);
    },
    openFilterGroup(key) {
      this.filterGroup = key;
      this.filterSheet = false;
      this.$nextTick(() => {
        this.filterGroupSheet = true;
      });
    },
    backToFilterMain() {
      this.filterGroupSheet = false;
      this.$nextTick(() => {
        this.filterSheet = true;
      });
    },
    hasValue(v) {
      if (Array.isArray(v)) return v.length > 0;
      return v !== null && v !== undefined && v !== "";
    },
    groupHasValue(key) {
      const list = this.paymentsList;
      if (key === "parties") {
        return (
          this.hasValue(list.account) ||
          this.hasValue(list.bank) ||
          this.hasValue(list.currencyFilter)
        );
      }
      if (key === "amount") {
        return this.hasValue(list.fromAmount) || this.hasValue(list.toAmount);
      }
      if (key === "date") {
        return (
          this.hasValue(list.fromDate) ||
          this.hasValue(list.toDate) ||
          this.hasValue(list.fromTime) ||
          this.hasValue(list.toTime)
        );
      }
      if (key === "category") {
        return (
          this.hasValue(list.tag) ||
          this.hasValue(list.branch) ||
          this.hasValue(list.accountCategory) ||
          this.hasValue(list.registerar)
        );
      }
      if (key === "status") return this.hasValue(list.lockStatus);
      return false;
    },
    groupSubtitle(key) {
      const list = this.paymentsList;
      if (key === "parties") {
        return (
          this.$t("account") +
          " · " +
          this.$t("bank") +
          " · " +
          this.$t("currency")
        );
      }
      if (key === "amount") {
        const parts = [];
        if (this.hasValue(list.fromAmount)) {
          parts.push(
            this.$t("from") + " " + this.$t("amount") + ": " + list.fromAmount
          );
        }
        if (this.hasValue(list.toAmount)) {
          parts.push(
            this.$t("until") + " " + this.$t("amount") + ": " + list.toAmount
          );
        }
        return parts.length ? parts.join(" · ") : this.$t("amount");
      }
      if (key === "date") {
        const parts = [];
        if (this.hasValue(list.fromDate)) parts.push(list.fromDate);
        if (this.hasValue(list.toDate)) parts.push(list.toDate);
        if (this.hasValue(list.fromTime)) parts.push(list.fromTime);
        if (this.hasValue(list.toTime)) parts.push(list.toTime);
        return parts.length ? parts.join(" · ") : this.$t("date");
      }
      if (key === "category") {
        return (
          this.$t("tag") +
          " · " +
          this.$t("branch") +
          " · " +
          this.$t("accountCategory") +
          " · " +
          this.$t("registerar")
        );
      }
      if (key === "status") {
        const lockItem = (list.lockStatusItems || []).find(
          (i) => i.value === list.lockStatus
        );
        return lockItem ? lockItem.text : this.$t("lockStatusDocuments");
      }
      return "";
    },
    numpadFieldText(key) {
      const value = this.paymentsList[key];
      if (!this.hasValue(value)) return "—";
      const field = this.numpadFields.find((f) => f.key === key);
      if (field && field.isAmount && this.$numberFormat) {
        return this.$numberFormat(value, "plain", 0);
      }
      return String(value);
    },
    openNumpad(field) {
      this.numpadField = field.key;
      this.numpadLabel = field.label;
      this.numpadIsAmount = !!field.isAmount;
      const current = this.paymentsList[field.key];
      this.numpadDraft = this.hasValue(current) ? String(current) : "";
      this.numpadOpen = true;
    },
    numpadDigit(num) {
      this.numpadAppend(String(num));
    },
    numpadAppend(chunk) {
      const piece = String(chunk);
      if (!piece) return;
      if (String(this.numpadDraft).length + piece.length > 16) return;
      if (!this.numpadDraft && /^\d+$/.test(piece)) {
        this.numpadDraft = piece.replace(/^0+(?=\d)/, "");
      } else if (this.numpadDraft === "0" && /^\d+$/.test(piece)) {
        this.numpadDraft = piece;
      } else {
        this.numpadDraft = String(this.numpadDraft) + piece;
      }
    },
    numpadDot() {
      if (!this.numpadIsAmount) return;
      if (!this.numpadDraft) {
        this.numpadDraft = "0.";
        return;
      }
      if (String(this.numpadDraft).includes(".")) return;
      this.numpadDraft = String(this.numpadDraft) + ".";
    },
    numpadBackspace() {
      const current = String(this.numpadDraft || "");
      this.numpadDraft = current.length <= 1 ? "" : current.slice(0, -1);
    },
    numpadClear() {
      this.numpadDraft = "";
    },
    numpadStep(delta) {
      const next = (parseFloat(this.numpadDraft) || 0) + delta;
      this.numpadDraft = next < 0 ? "0" : String(next);
    },
    openDateLayer(field) {
      this.dateLayerField = field.key;
      this.dateLayerLabel = field.label;
      this.dateLayerIsTime = !!field.isTime;
      this.dateLayerOpen = true;
    },
    confirmNumpad() {
      if (this.numpadField) {
        const raw = String(this.numpadDraft || "").trim();
        this.paymentsList[this.numpadField] =
          raw === "" ? null : parseFloat(raw);
      }
      this.numpadField = null;
      this.numpadOpen = false;
    },
    applyFilter() {
      this.filterSheet = false;
      this.filterGroupSheet = false;
      this.appending = false;
      this.paymentsList.tableOptions = Object.assign(
        {},
        this.paymentsList.tableOptions,
        { page: 1 }
      );
      this.paymentsList.getItems();
    },
    clearFilter() {
      const list = this.paymentsList;
      list.account = null;
      list.bank = null;
      list.currencyFilter = null;
      list.fromAmount = null;
      list.toAmount = null;
      list.fromDate = null;
      list.toDate = null;
      list.fromTime = null;
      list.toTime = null;
      list.tag = null;
      list.branch = null;
      list.accountCategory = null;
      list.registerar = null;
      list.lockStatus = null;
      this.applyFilter();
    },
    selectedRowNumber(item) {
      if (!item) return "";
      const idx = this.displayItems.findIndex((i) => i.id === item.id);
      return idx >= 0 ? idx + 1 : "";
    },
    askDelete(item) {
      this.moreSheet = false;
      this.detailSheet = false;
      this.deleteGroupMode = false;
      this.paymentsList.deleteItem = item;
      this.deleteSheet = true;
    },
    askGroupDelete() {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.paymentsList.deleteItem = null;
      this.deleteGroupMode = true;
      this.deleteSheet = true;
    },
    cancelDelete() {
      this.deleteSheet = false;
      this.deleteGroupMode = false;
    },
    confirmDelete() {
      this.deleteSheet = false;
      if (this.paymentsList.selected && this.paymentsList.selected.length > 0) {
        this.paymentsList.removegroup();
      } else {
        this.paymentsList.remove();
      }
      if (this.deleteGroupMode) {
        this.deleteGroupMode = false;
        this.exitSelectionMode();
      }
    },
    bulkLock(type) {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.paymentsList.lockGroup(type);
    },
    bulkRefreshRemoved() {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.paymentsList.refreshRemovedItem();
    },
    toggleSelectionMode() {
      this.selectionMode = !this.selectionMode;
      if (!this.selectionMode) {
        this.selectedItems = [];
        this._selectionSynced = false;
        this.paymentsList.selected = [];
      }
    },
    exitSelectionMode() {
      this.selectionMode = false;
      this.selectedItems = [];
      this._selectionSynced = false;
      this.paymentsList.selected = [];
    },
    isSelected(item) {
      return this.selectedItems.some((i) => i.id === item.id);
    },
    toggleSelect(item) {
      if (this.isSelected(item)) {
        this.selectedItems = this.selectedItems.filter(
          (i) => i.id !== item.id
        );
      } else {
        this.selectedItems = this.selectedItems.concat([item]);
      }
    },
    selectAllLoaded() {
      const seen = {};
      this.selectedItems.forEach((i) => {
        seen[i.id] = true;
      });
      this.selectedItems = this.selectedItems.concat(
        this.displayItems.filter((i) => !seen[i.id])
      );
    },
    clearSelection() {
      this.selectedItems = [];
    },
    syncSelection() {
      this.paymentsList.selected = this.selectedItems.slice();
      this._selectionSynced = true;
    },
    amountText(item) {
      if (!item) return "—";
      const cur =
        item.currency && item.currency.name ? " " + item.currency.name : "";
      return this.$numberFormat(parseFloat(item.amount || 0)) + cur;
    },
    hasRelations(item) {
      return !!(item && item.relations && item.relations.length);
    },
    openRelationsSheet(item) {
      if (!this.hasRelations(item)) return;
      this.relationsItem = item;
      this.relationsSheet = true;
    },
    toLatinDigits(value) {
      return String(value == null ? "" : value)
        .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
        .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d));
    },
    formatRelationAmount(value) {
      if (value == null || value === "") return "—";
      const raw = parseFloat(
        this.toLatinDigits(value).replace(/,/g, "").trim()
      );
      if (Number.isNaN(raw)) return String(value);
      return this.$numberFormat ? this.$numberFormat(raw) : String(raw);
    },
    parseRelationName(name) {
      const text = String(name || "").trim();
      const latin = this.toLatinDigits(text);
      const result = { typeLabel: "", number: null, amount: null };
      if (!text) return result;

      // "فروش شماره :7308 مبلغ : 3,960,000" (and Persian-digit variants)
      const full = latin.match(
        /^(.+?)\s*(?:شماره|number)\s*:?\s*([0-9]+)\s*(?:مبلغ|amount)\s*:?\s*([0-9,]+)/i
      );
      if (full) {
        result.typeLabel = full[1].trim();
        result.number = full[2];
        result.amount = full[3].replace(/,/g, "");
        return result;
      }

      const numMatch = latin.match(/(?:شماره|number)\s*:?\s*([0-9]+)/i);
      if (numMatch) result.number = numMatch[1];
      const amountMatch = latin.match(/(?:مبلغ|amount)\s*:?\s*([0-9,]+)/i);
      if (amountMatch) result.amount = amountMatch[1].replace(/,/g, "");

      let typeLabel = text
        .replace(/شماره\s*:?\s*[۰-۹0-9]+/gi, " ")
        .replace(/مبلغ\s*:?\s*[۰-۹0-9,،]+/gi, " ")
        .replace(/number\s*:?\s*[0-9]+/gi, " ")
        .replace(/amount\s*:?\s*[0-9,]+/gi, " ")
        .replace(/\s+/g, " ")
        .trim();
      result.typeLabel = typeLabel;
      return result;
    },
    relationRows(item) {
      const list = (item && item.relations) || [];
      return list.map((relation) => {
        const parsed = this.parseRelationName(relation.name);
        const docType = relation.doc_type || relation.type || "";
        // Prefer values parsed from API display name (desktop chip text).
        const typeLabel =
          parsed.typeLabel ||
          (docType ? this.$t(docType) : "") ||
          this.$t("relations");

        let number = parsed.number;
        if (number == null || number === "") {
          if (relation.number != null && relation.number !== "") {
            number = relation.number;
          } else if (relation.refrence != null && relation.refrence !== "") {
            number = relation.refrence;
          }
        }

        let amountText = "—";
        if (parsed.amount != null && parsed.amount !== "") {
          amountText = this.formatRelationAmount(parsed.amount);
        } else if (relation.amount != null && relation.amount !== "") {
          amountText = this.formatRelationAmount(relation.amount);
        }

        return {
          typeLabel,
          number: number != null && number !== "" ? number : "—",
          amountText,
        };
      });
    },
    openDetails(item) {
      this.detailItem = item;
      this.detailSheet = true;
    },
    editLink(item) {
      if (!item) return "";
      const type = item.type || this.paymentsList.type;
      const path = type === "recieve" ? "receive/cash/edit" : "pay/cash/edit";
      return this.$baseURL + "/" + path + "/" + item.id;
    },
    notifyCopied() {
      if (typeof this.copied === "function") this.copied();
      else if (
        this.paymentsList &&
        typeof this.paymentsList.copied === "function"
      ) {
        this.paymentsList.copied();
      }
    },
    onCopyLink() {
      this.moreSheet = false;
      this.notifyCopied();
    },
    onCopyLinkQuick() {
      this.notifyCopied();
    },
    runNamedAction(name) {
      const item = this.detailItem;
      if (!item) return;
      const list = this.paymentsList;
      this.moreSheet = false;
      if (name === "lock") {
        this.detailSheet = false;
        list.lock(item, item.lock ? "unlock" : "lock");
        return;
      }
      if (name === "remove") {
        this.askDelete(item);
        return;
      }
      if (name === "viewDoc") {
        this.detailSheet = false;
        list.viewDocument(item);
        return;
      }
      if (name === "receipt") {
        this.detailSheet = false;
        list.receiptPrint(item);
        return;
      }
      this.detailSheet = false;
      if (name === "edit") list.edit(item);
      else if (name === "accountTurnover" && item.account) {
        list.openComponent("accountingTurnover", item.account.fin_id);
      }
    },
    detailRows(item) {
      if (!item) return [];
      const rows = [];
      const push = (label, value, kind, tone) =>
        rows.push({
          label,
          value: value == null || value === "" ? "—" : value,
          kind: kind || null,
          tone: tone || null,
        });
      const type = item.type || this.paymentsList.type;
      const amountTone =
        type === "pay" ? "pay" : type === "recieve" ? "receive" : null;
      // Same order as desktop paymentsList headers (account stays in name-block).
      // Soft band on number / doc / bank; amount row is fully tinted pay/receive.
      push(
        this.$t("number") + " " + this.$t(this.paymentsList.type),
        item.id,
        "band"
      );
      push(
        this.$t("document-number"),
        item.doc_header && item.doc_header.number,
        "band"
      );
      push(
        this.$t("bank") + " / " + this.$t("cash"),
        item.bank && item.bank.name,
        "band"
      );
      push(this.$t("amount"), this.amountText(item), "amount", amountTone);
      push(this.$t("date"), item.date);
      push(this.$t("time"), item.time);
      push(this.$t("branch"), item.branch && item.branch.name);
      push(this.$t("issueTracking"), item.issue_tracking);
      push(this.$t("title"), item.title);
      push(this.$t("description"), item.content);
      if (item.user) {
        push(
          this.$t("registerar"),
          ((item.user.firstname || "") + " " + (item.user.lastname || "")).trim()
        );
      } else {
        push(this.$t("registerar"), null);
      }
      if (this.paymentsList.type === "removedPayments") {
        push(this.$t("type"), this.$t(item.type));
      }
      return rows;
    },
    loadMore() {
      if (!this.displayItems.length) return;
      if (this.paymentsList.loading || this.appending) return;
      if (this.currentPage >= (this.paymentsList.pageCount || 1)) return;
      this.appending = true;
      this.paymentsList.tableOptions = Object.assign(
        {},
        this.paymentsList.tableOptions,
        { page: this.currentPage + 1 }
      );
    },
  },
};
</script>

<style scoped>
.pml {
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px;
  background: #ffffff;
  min-height: calc(100vh - 52px);
  box-sizing: border-box;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
}
.pml--selecting .pml__list {
  padding-bottom: 88px;
}
.pml__header {
  position: sticky;
  top: 52px;
  z-index: 4;
  background: #ffffff;
  padding: 6px 0 10px;
  border-bottom: 1px solid #eef1f4;
  margin-bottom: 10px;
}
.pml__bar {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 8px;
}
.pml__bar-actions {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-width: 80px;
}
.pml__title {
  flex: 1 1 auto;
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
  text-align: center;
}
.pml__bar-btn {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.pml__bar-btn:active {
  background: rgba(91, 124, 153, 0.1);
}
.pml__bar-btn--create {
  background: rgba(91, 124, 153, 0.1);
}
.pml__bar-btn--back {
  flex: 0 0 40px;
}
.pml__search-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pml__search {
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-radius: 10px;
  background: #f3f5f7;
}
.pml__search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #263238;
  text-align: right;
}
.pml__search-clear {
  flex: 0 0 auto;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
}
.pml__filter-btn {
  flex: 0 0 44px;
  height: 44px;
  border: 1px solid #e0e4e8;
  border-radius: 10px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.pml__filter-btn--active {
  background: rgba(21, 101, 192, 0.1);
  border-color: rgba(21, 101, 192, 0.35);
}
.pml__select-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  padding: 6px 10px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
}
.pml__select-bar-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 6px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.pml__select-bar-btn:active {
  background: rgba(91, 124, 153, 0.12);
}
.pml__select-count {
  font-size: 12px;
  font-weight: 700;
  color: #5b7c99;
}
.pml__state {
  display: flex;
  justify-content: center;
  padding: 40px 0;
  color: #607d8b;
  font-size: 13px;
}
.pml__state--empty {
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: calc(100vh - 220px);
  padding: 32px 16px 24px;
  background: #ffffff;
}
.pml__empty-logo {
  width: 72px;
  height: 72px;
  object-fit: contain;
  opacity: 0.85;
}
.pml__empty-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #78909c;
  text-align: center;
}
.pml__state--more {
  padding: 14px 0;
}
.pml__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.pml__sentinel {
  height: 1px;
}
.pml-card {
  position: relative;
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  padding: 12px;
  transition: transform 0.15s ease;
}
.pml-card--selected {
  transform: scale(0.99);
  border-color: rgba(21, 101, 192, 0.45);
}
.pml-card__select-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: 10px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.pml-card__select-overlay--on {
  background: rgba(38, 50, 56, 0.12);
}
.pml-card__check {
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #0d47a1;
  box-shadow: 0 2px 6px rgba(13, 71, 161, 0.4);
}
.pml-bulkbar {
  position: fixed;
  right: 12px;
  left: 12px;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  z-index: 6;
  display: flex;
  gap: 4px;
  padding: 8px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 8px 24px rgba(38, 50, 56, 0.14);
}
.pml-bulkbar__btn {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-height: 48px;
  padding: 6px 2px;
  border: 0;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.pml-bulkbar__btn:active {
  background: rgba(91, 124, 153, 0.15);
}
.pml-bulkbar__btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.pml-bulkbar__btn--danger {
  color: #e53935;
  background: rgba(229, 57, 53, 0.06);
}
.pml-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.pml-card__title-block {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}
.pml-card__name-link {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  min-width: 0;
  max-width: 100%;
  padding: 2px 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #5b7c99;
  cursor: pointer;
}
.pml-card__sub {
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.35;
}
.pml-card__name-link:disabled {
  opacity: 0.7;
  cursor: default;
}
.pml-card__name {
  font-size: 14px;
  font-weight: 700;
  color: #5b7c99;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pml-card__number {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #5b7c99;
  background: rgba(91, 124, 153, 0.1);
  border-radius: 10px;
  padding: 2px 8px;
}
.pml-card__amounts {
  margin-top: 10px;
  border-top: 1px dashed rgba(144, 164, 174, 0.35);
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.pml-card__amount-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.pml-card__amount-label {
  font-size: 12px;
  color: #78909c;
}
.pml-card__amount-value {
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
  padding: 2px 8px;
  border-radius: 8px;
}
.pml-card__amount-value--pay {
  background: rgba(229, 57, 53, 0.1);
  color: #c62828;
}
.pml-card__amount-value--receive {
  background: rgba(67, 160, 71, 0.12);
  color: #2e7d32;
}
.pml-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
}
.pml-card__actions {
  display: flex;
  gap: 6px;
  flex: 0 1 auto;
  min-width: 0;
}
.pml-btn {
  flex: 0 0 auto;
  height: 28px;
  padding: 0 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  border: 0;
  -webkit-tap-highlight-color: transparent;
}
.pml-btn--danger {
  border: 1px solid rgba(229, 57, 53, 0.35);
  background: rgba(229, 57, 53, 0.06);
  color: #e53935;
}
.pml-btn--primary {
  background: #5b7c99;
  color: #ffffff;
}
.pml-btn--outline {
  background: #ffffff;
  color: #5b7c99;
  border: 1px solid #cfd8dc;
}
.pml-btn:disabled {
  opacity: 0.45;
  cursor: default;
}
.pml-rel-btn {
  flex: 0 0 auto;
  height: 28px;
  padding: 0 8px;
  border: 0;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.08);
  color: #5b7c99;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.pml-rel-btn:active {
  background: rgba(91, 124, 153, 0.16);
}
.pml-rel-btn:disabled {
  background: transparent;
  color: #90a4ae;
  opacity: 0.55;
  cursor: default;
}
.pml-rel-empty {
  padding: 18px 0;
  font-size: 12.5px;
  color: #90a4ae;
  text-align: center;
}
.pml-rel-item {
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 10px;
}
.pml-rel-item:last-child {
  margin-bottom: 0;
}
.pml-rel-item__title {
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
}
.pml-rel-item__line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 28px;
}
.pml-rel-item__key {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.pml-rel-item__val {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
  direction: ltr;
}
.pml-sheet {
  border-radius: 10px 10px 0 0 !important;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}
.pml-sheet__handle {
  flex: 0 0 auto;
  width: 44px;
  height: 4px;
  border-radius: 10px;
  background: #cfd8dc;
  margin: 10px auto 2px;
}
.pml-sheet__header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 10px;
  border-bottom: 1px solid #eef1f4;
}
.pml-sheet__header-side {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  min-width: 0;
}
.pml-sheet__header-side--end {
  justify-content: flex-end;
}
.pml-sheet__header-spacer {
  flex: 0 0 40px;
  width: 40px;
}
.pml-sheet__title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.pml-sheet__num-chip {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 10px;
  background: rgba(135, 158, 182, 0.14);
  border: 1px solid rgba(135, 158, 182, 0.28);
  color: #5b7c99;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.pml-sheet__back {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.pml-sheet__back:active {
  background: rgba(91, 124, 153, 0.1);
}
.pml-sheet__quickbar {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  padding: 8px 12px 0;
}
.pml-sheet__quick {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 46px;
  padding: 4px 2px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 9px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.pml-sheet__quick .v-icon {
  font-size: 18px !important;
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
}
.pml-sheet__quick:active {
  background: rgba(91, 124, 153, 0.14);
}
.pml-sheet__quick:disabled {
  opacity: 0.45;
  cursor: default;
}
.pml-sheet__name-block {
  flex: 0 0 auto;
  background: #edf2f7;
  border-radius: 10px;
  padding: 10px 14px;
  margin: 6px 16px 0;
}
.pml-sheet__name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.pml-sheet__name-value {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 70%;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pml-sheet__name-block .pml-sheet__linkrow {
  margin: 8px 0 0;
  background: rgba(255, 255, 255, 0.75);
}
.pml-sheet__name-block .pml-sheet__linkrow:active {
  background: rgba(255, 255, 255, 0.95);
}
.pml-sheet__linkrow {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  margin: 6px 0 2px;
  padding: 0 10px;
  border: 1px solid rgba(0, 150, 136, 0.16);
  border-radius: 10px;
  background: rgba(0, 150, 136, 0.07);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.pml-sheet__linkrow:active {
  background: rgba(0, 150, 136, 0.15);
}
.pml-sheet__linkrow-text {
  flex: 1 1 auto;
  font-size: 12.5px;
  font-weight: 700;
  color: #00796b;
  text-align: right;
}
.pml-sheet__body {
  overflow-y: auto;
  padding: 8px 16px 20px;
}
.pml-sheet__card {
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  padding: 4px 14px;
}
.pml-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e8edf2;
}
.pml-sheet__card .pml-sheet__row:last-child {
  border-bottom: 0;
}
/* Soft band like invoice money rows; amount row is fully tinted. */
.pml-sheet__row--band {
  margin: 0 -14px;
  padding: 8px 14px;
  border-bottom-color: rgba(91, 124, 153, 0.12);
  background: rgba(91, 124, 153, 0.05);
}
.pml-sheet__row--pay {
  background: rgba(229, 57, 53, 0.1);
  border-bottom-color: rgba(198, 40, 40, 0.18);
}
.pml-sheet__row--pay .pml-sheet__row-label,
.pml-sheet__row--pay .pml-sheet__row-value {
  color: #c62828;
}
.pml-sheet__row--receive {
  background: rgba(67, 160, 71, 0.12);
  border-bottom-color: rgba(46, 125, 50, 0.18);
}
.pml-sheet__row--receive .pml-sheet__row-label,
.pml-sheet__row--receive .pml-sheet__row-value {
  color: #2e7d32;
}
.pml-sheet__row-label {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.pml-sheet__row-value {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.pml-sheet__tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
  max-width: 62%;
}
.pml-sheet__actions {
  display: flex;
  flex-direction: column;
}
.pml-sheet__action {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 42px;
  padding: 0 6px;
  border: 0;
  border-bottom: 1px solid #e8edf2;
  border-radius: 10px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  cursor: pointer;
  text-align: right;
}
.pml-sheet__action:active {
  background: rgba(91, 124, 153, 0.08);
}
.pml-sheet__action:disabled {
  opacity: 0.45;
  cursor: default;
}
.pml-sheet__action--danger {
  color: #e53935;
}
.pml-sheet__action--log {
  cursor: default;
  padding: 0;
}
.pml-sheet__action--log > div {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
}
.pml-sheet__action--log ::v-deep .v-list-item {
  flex: 1 1 auto;
  min-height: 42px;
  height: 42px;
  padding: 0;
}
.pml-sheet__action--log ::v-deep .v-list-item__icon {
  margin: 0 !important;
  min-width: 19px;
  align-self: center;
}
.pml-sheet__action--log ::v-deep .v-list-item__icon .v-icon {
  font-size: 19px !important;
  color: #5b7c99 !important;
}
.pml-sheet__action--log ::v-deep .v-list-item__title {
  margin-inline-start: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  text-align: right;
}
.pml-sheet--filter {
  max-height: 92vh;
  border-radius: 20px 20px 0 0 !important;
}
.pml-sheet__footer {
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #eef1f4;
  background: #ffffff;
}
.pml-sheet__btn {
  flex: 1 1 0;
  height: 44px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.pml-sheet__btn--primary {
  border: 0;
  background: #5b7c99;
  color: #ffffff;
}
.pml-sheet__btn--primary:active {
  background: #4d6a84;
}
.pml-sheet__btn--ghost {
  border: 1px solid #cfd8dc;
  background: #ffffff;
  color: #546e7a;
}
.pml-sheet__btn--ghost:active {
  background: rgba(91, 124, 153, 0.08);
}
.pml-filter-nav {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0;
  background: #ffffff;
}
.pml-filter-nav__item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 12px;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: #fff;
  text-align: right;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.pml-filter-nav__item:active {
  background: #f7f9fc;
}
.pml-filter-nav__item:last-child {
  border-bottom: 0;
}
.pml-filter-nav__icon {
  flex: 0 0 auto;
}
.pml-filter-nav__texts {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
}
.pml-filter-nav__item-title {
  display: block;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  line-height: 1.35;
  text-align: right;
}
.pml-filter-nav__item-sub {
  display: block;
  width: 100%;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  color: #90a4ae;
  line-height: 1.35;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pml-filter-nav__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b7c99;
}
.pml-filter-fields {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 14px 16px 6px;
  background: #ffffff;
}
.pml-filter-fields > * {
  margin-bottom: 14px;
}
.pml-picker-host {
  display: none !important;
}
.pml-picker-cover__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
}
.pml-f-field-wrap {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.pml-f-field-wrap__label {
  display: block;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  text-align: right;
  line-height: 1.4;
  margin: 0 2px 4px;
}
.pml-f-picker-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  min-height: 40px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 10px;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.pml-f-picker-field:active {
  background: #eef4f8;
  border-color: #90caf9;
}
.pml-f-picker-field__text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  color: #263238;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pml-select-layer {
  padding: 6px 16px 20px;
}
.pml-select-layer__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  padding: 10px 4px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: transparent;
  text-align: right;
  cursor: pointer;
}
.pml-select-layer__text {
  font-size: 13.5px;
  font-weight: 500;
  color: #37474f;
}
.pml-select-layer__clear {
  display: block;
  width: 100%;
  padding: 14px 4px 4px;
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #c62828;
  text-align: center;
  cursor: pointer;
}
.pml-mobile-date {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 16px;
  background: #ffffff;
}
.pml-mobile-date__selected {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #e0e6ed;
  border-radius: 12px;
  background: #f7f9fc;
}
.pml-mobile-date__label {
  width: 100%;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.pml-mobile-date__value {
  font-size: 18px;
  font-weight: 800;
  color: #263238;
  letter-spacing: 0.02em;
}
.pml-mobile-date__clear {
  border: 0;
  background: transparent;
  color: #e91e63;
  font-size: 13px;
  font-weight: 700;
  padding: 4px 0;
  cursor: pointer;
}
.pml-mobile-date__picker {
  width: 100%;
}
.pml-mobile-date__picker ::v-deep .vpd-main,
.pml-mobile-date__picker ::v-deep .vpd-wrapper,
.pml-mobile-date__picker ::v-deep .vpd-container,
.pml-mobile-date__picker ::v-deep .vpd-content {
  width: 100% !important;
  max-width: 100% !important;
}
.pml-numpad {
  border-radius: 20px 20px 0 0 !important;
  background: #fff !important;
  padding-bottom: env(safe-area-inset-bottom, 0px);
}
.pml-numpad__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 12px 6px;
}
.pml-numpad__header-spacer {
  flex: 0 0 40px;
}
.pml-numpad__title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.pml-numpad__display {
  margin: 4px 16px 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f3f5f7;
  text-align: left;
}
.pml-numpad__display-label {
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  text-align: right;
}
.pml-numpad__display-value {
  margin-top: 4px;
  font-size: 28px;
  font-weight: 800;
  color: #263238;
  letter-spacing: 0.02em;
  direction: ltr;
}
.pml-numpad__pad {
  display: flex;
  gap: 8px;
  padding: 0 12px 8px;
}
.pml-numpad__digits {
  flex: 3 1 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.pml-numpad__actions {
  flex: 1 1 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}
.pml-numpad__key {
  height: 48px;
  border: 1px solid #e0e4e8;
  border-radius: 12px;
  background: #fff;
  font-size: 18px;
  font-weight: 700;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.pml-numpad__key:active {
  background: #eef4f8;
}
.pml-numpad__key--multi {
  font-size: 14px;
}
.pml-numpad__key--action {
  background: #f7f9fc;
  color: #5b7c99;
}
.pml-numpad__footer {
  padding: 8px 12px 12px;
}
.pml-numpad__confirm {
  width: 100%;
  height: 48px;
  border: 0;
  border-radius: 12px;
  background: #5b7c99;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}
.pml-numpad__confirm:active {
  background: #4d6a84;
}
.pml-confirm-sheet {
  border-radius: 20px 20px 0 0 !important;
  padding: 8px 16px 16px;
  background: #fff !important;
}
.pml-confirm-sheet__handle {
  width: 40px;
  height: 4px;
  margin: 4px auto 14px;
  border-radius: 999px;
  background: #cfd8dc;
}
.pml-confirm-sheet__icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: rgba(229, 57, 53, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}
.pml-confirm-sheet__title {
  text-align: center;
  font-size: 18px;
  font-weight: 800;
  color: #212121;
  margin: 0 0 8px;
}
.pml-confirm-sheet__subtitle {
  margin: 0 0 16px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #607d8b;
  line-height: 1.6;
}
.pml-confirm-sheet__rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
}
.pml-confirm-sheet__items {
  max-height: 168px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
  padding: 4px 12px;
}
.pml-confirm-sheet__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px solid #e8edf2;
}
.pml-confirm-sheet__item:last-child {
  border-bottom: 0;
}
.pml-confirm-sheet__item-main {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}
.pml-confirm-sheet__item-index {
  flex: 0 0 auto;
  min-width: 16px;
  font-size: 11px;
  font-weight: 700;
  color: #90a4ae;
  text-align: center;
}
.pml-confirm-sheet__item-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12.5px;
  font-weight: 600;
  color: #37474f;
}
.pml-confirm-sheet__item-number {
  flex: 0 0 auto;
  font-size: 11.5px;
  font-weight: 700;
  color: #5b7c99;
}
.pml-confirm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f5f7f9;
  font-size: 13px;
  font-weight: 600;
  color: #607d8b;
}
.pml-confirm-sheet__row strong {
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: left;
}
.pml-confirm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.pml-confirm-sheet__yes {
  border-radius: 12px !important;
  font-weight: 800 !important;
  letter-spacing: 0 !important;
}
.pml-confirm-sheet__no {
  border-radius: 12px !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
}
</style>
